import React, { useState } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { confirmDialog } from 'primereact/confirmdialog';
import { Message } from 'primereact/message';
import { Tag } from 'primereact/tag';

interface ExportTemplate {
  id: string;
  name: string;
  description: string;
  config: any;
  createdAt: Date;
  updatedAt: Date;
}

interface TemplateManagerProps {
  templates: ExportTemplate[];
  currentTemplate: ExportTemplate | null;
  onLoadTemplate: (template: ExportTemplate) => void;
  onSaveTemplate: (templateData: { name: string; description: string }) => void;
  onDeleteTemplate: (templateId: string) => void;
}

export const TemplateManager: React.FC<TemplateManagerProps> = ({
  templates,
  currentTemplate,
  onLoadTemplate,
  onSaveTemplate,
  onDeleteTemplate
}) => {
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [newTemplate, setNewTemplate] = useState({
    name: '',
    description: ''
  });

  const handleSaveTemplate = () => {
    if (newTemplate.name.trim()) {
      onSaveTemplate(newTemplate);
      setNewTemplate({ name: '', description: '' });
      setShowSaveDialog(false);
    }
  };

  const handleDeleteTemplate = (template: ExportTemplate) => {
    confirmDialog({
      message: `Tem certeza que deseja excluir o template "${template.name}"?`,
      header: 'Confirmar Exclusão',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sim',
      rejectLabel: 'Não',
      accept: () => onDeleteTemplate(template.id),
      acceptClassName: 'p-button-danger'
    });
  };

  const renderTemplateActions = (template: ExportTemplate) => (
    <div className="flex gap-2">
      <Button
        icon="pi pi-upload"
        className="p-button-sm p-button-success"
        onClick={() => onLoadTemplate(template)}
        tooltip="Carregar template"
      />
      <Button
        icon="pi pi-trash"
        className="p-button-sm p-button-danger"
        onClick={() => handleDeleteTemplate(template)}
        tooltip="Excluir template"
      />
    </div>
  );

  const renderTemplateInfo = (template: ExportTemplate) => (
    <div>
      <div className="font-semibold">{template.name}</div>
      <div className="text-sm text-color-secondary mt-1">
        {template.description || 'Sem descrição'}
      </div>
      <div className="mt-2">
        <small className="text-color-secondary">
          Criado em: {template.createdAt.toLocaleDateString('pt-PT')}
        </small>
      </div>
    </div>
  );

  const renderTemplateDetails = (template: ExportTemplate) => {
    const config = template.config;
    return (
      <div className="flex gap-2 flex-wrap">
        {config.selectedColumns && (
          <Tag 
            severity="info" 
            value={`${config.selectedColumns.length} colunas`} 
          />
        )}
        {config.whereConditions && config.whereConditions.length > 0 && (
          <Tag 
            severity="warning" 
            value={`${config.whereConditions.length} filtros`} 
          />
        )}
        {config.orientation && (
          <Tag 
            severity="success" 
            value={config.orientation} 
          />
        )}
      </div>
    );
  };

  const saveDialogFooter = (
    <div>
      <Button
        label="Cancelar"
        icon="pi pi-times"
        outlined
        onClick={() => setShowSaveDialog(false)}
      />
      <Button
        label="Salvar"
        icon="pi pi-check"
        onClick={handleSaveTemplate}
        disabled={!newTemplate.name.trim()}
      />
    </div>
  );

  return (
    <div>
      <div className="flex justify-content-between align-items-center mb-3">
        <h5 className="m-0">Templates Salvos</h5>
        <Button
          label="Salvar Configuração Atual"
          icon="pi pi-save"
          onClick={() => setShowSaveDialog(true)}
          size="small"
        />
      </div>

      {currentTemplate && (
        <Message 
          severity="info" 
          text={`Template ativo: ${currentTemplate.name}`}
          className="mb-3"
        />
      )}

      {templates.length === 0 ? (
        <Card>
          <div className="text-center p-4 text-color-secondary">
            <i className="pi pi-bookmark mb-3" style={{ fontSize: '3rem' }} />
            <p>Nenhum template salvo ainda</p>
            <small>
              Configure suas colunas e filtros, depois salve como template para reutilizar
            </small>
          </div>
        </Card>
      ) : (
        <DataTable 
          value={templates} 
          className="p-datatable-sm"
          responsiveLayout="scroll"
          emptyMessage="Nenhum template encontrado"
        >
          <Column
            field="name"
            header="Template"
            body={renderTemplateInfo}
            style={{ minWidth: '300px' }}
          />
          
          <Column
            header="Configurações"
            body={renderTemplateDetails}
            style={{ minWidth: '200px' }}
          />

          <Column
            header="Ações"
            body={renderTemplateActions}
            style={{ width: '120px' }}
          />
        </DataTable>
      )}

      <Dialog
        visible={showSaveDialog}
        onHide={() => setShowSaveDialog(false)}
        header="Salvar Template"
        footer={saveDialogFooter}
        style={{ width: '400px' }}
        modal
      >
        <div className="field">
          <label htmlFor="templateName" className="block font-semibold mb-2">
            Nome do Template *
          </label>
          <InputText
            id="templateName"
            value={newTemplate.name}
            onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
            placeholder="Ex: Relatório Mensal"
            className="w-full"
          />
        </div>

        <div className="field">
          <label htmlFor="templateDescription" className="block font-semibold mb-2">
            Descrição (Opcional)
          </label>
          <InputTextarea
            id="templateDescription"
            value={newTemplate.description}
            onChange={(e) => setNewTemplate({ ...newTemplate, description: e.target.value })}
            placeholder="Descreva o que este template faz..."
            rows={3}
            className="w-full"
          />
        </div>

        <Message 
          severity="info" 
          text="Este template salvará suas colunas selecionadas, filtros e configurações de layout."
          className="mt-3"
        />
      </Dialog>
    </div>
  );
};